// ============================================
// @engine/render2d - Core Type Definitions
// ============================================

import type { GpuPassTime } from './core/GpuTimer';

export type { GpuPassTime };

// ============================================
// GPU Backend
// ============================================

export type GPUBackend = 'webgpu';

// ============================================
// Handles (opaque resource identifiers)
// ============================================

export type TextureHandle = number;
export type BufferHandle = number;
export type PipelineHandle = number;
export type ShaderHandle = number;
export type RenderTargetHandle = number;
export type MRTRenderTargetHandle = number;
export type ComputePipelineHandle = number;

// Null handle constant
export const NULL_HANDLE = 0;

/**
 * Pipeline-overridable constants (WGSL `override` declarations).
 * Values are set per-pipeline via `device.createRenderPipeline({ constants })`,
 * enabling GPU-compiler dead-code elimination for feature toggles.
 *
 * Use this instead of preprocessor `#define` for feature flags:
 * ```wgsl
 * override USE_SHADOWS: bool = false;
 * override MAX_LIGHTS: u32 = 64;
 * ```
 */
export type OverrideConstants = Record<string, number | boolean>;

// ============================================
// Blend Modes
// ============================================

export enum BlendMode {
  /** No blending */
  None = 0,
  /** Standard alpha blending: srcAlpha, oneMinusSrcAlpha */
  Normal = 1,
  /** Additive blending: srcAlpha, one */
  Additive = 2,
  /** Multiply blending: dst, oneMinusSrcAlpha */
  Multiply = 3,
  /** Screen blending: one, oneMinusSrc */
  Screen = 4,
  /** Premultiplied alpha: one, oneMinusSrcAlpha */
  PremultipliedAlpha = 5,
}

// ============================================
// Texture
// ============================================

export type TextureFormat = 
  | 'rgba8unorm'
  | 'rgba8unorm-srgb'
  | 'bgra8unorm'
  | 'r8unorm'
  | 'rg8unorm'
  | 'rgba16float'
  | 'rgba32float'
  | 'depth24plus'
  | 'depth32float';

export type TextureUsage = 
  | 'sampled'
  | 'storage'
  | 'render-target'
  | 'copy-src'
  | 'copy-dst';

export type TextureFilter = 'nearest' | 'linear';
export type TextureWrap = 'repeat' | 'clamp' | 'mirror';

export interface TextureDescriptor {
  width: number;
  height: number;
  format?: TextureFormat;
  usage?: TextureUsage[];
  mipmaps?: boolean;
  filter?: TextureFilter;
  wrapU?: TextureWrap;
  wrapV?: TextureWrap;
  sRGB?: boolean; // Use sRGB color space (vs linear)
  label?: string;
}

// ============================================
// Buffer
// ============================================

export type BufferUsage = 
  | 'vertex'
  | 'index'
  | 'uniform'
  | 'storage'
  | 'copy-src'
  | 'copy-dst'
  | 'indirect';

export interface BufferDescriptor {
  size: number;
  usage: BufferUsage[];
  data?: ArrayBuffer | ArrayBufferView;
  label: string;
}

// ============================================
// Vertex Format
// ============================================

export type VertexAttributeFormat = 
  | 'float32'
  | 'float32x2'
  | 'float32x3'
  | 'float32x4'
  | 'uint8x4'
  | 'unorm8x4'
  | 'sint8x4'
  | 'snorm8x4'
  | 'uint16x2'
  | 'uint16x4'
  | 'sint16x2'
  | 'sint16x4'
  | 'uint32'
  | 'uint32x2'
  | 'uint32x3'
  | 'uint32x4'
  | 'float16x2'
  | 'float16x4';

export interface VertexAttribute {
  name: string;
  format: VertexAttributeFormat;
  offset: number;
  location: number;
}

export interface VertexBufferLayout {
  stride: number;
  stepMode?: 'vertex' | 'instance';
  attributes: VertexAttribute[];
}

// ============================================
// Pipeline
// ============================================

export type CullMode = 'none' | 'front' | 'back';
export type FrontFace = 'ccw' | 'cw';
export type PrimitiveTopology = 'triangle-list' | 'triangle-strip' | 'line-list' | 'line-strip' | 'point-list';

export type CompareFunction = 'never' | 'less' | 'equal' | 'less-equal' | 'greater' | 'not-equal' | 'greater-equal' | 'always';

export interface DepthStencilState {
  format: TextureFormat;
  depthWrite: boolean;
  depthCompare: CompareFunction;
}

export interface BlendComponent {
  srcFactor: BlendFactor;
  dstFactor: BlendFactor;
  operation: BlendOperation;
}

export type BlendFactor = 
  | 'zero'
  | 'one'
  | 'src'
  | 'one-minus-src'
  | 'src-alpha'
  | 'one-minus-src-alpha'
  | 'dst'
  | 'one-minus-dst'
  | 'dst-alpha'
  | 'one-minus-dst-alpha';

export type BlendOperation = 'add' | 'subtract' | 'reverse-subtract' | 'min' | 'max';

export interface BlendState {
  color: BlendComponent;
  alpha: BlendComponent;
}

export interface ColorTargetState {
  format: TextureFormat;
  /**
   * Blend state for this target.
   * - a BlendState → use it
   * - `null` → explicit NO blending (write-through)
   * - `undefined` → back-compat default (standard alpha blending)
   */
  blend?: BlendState | null;
  writeMask?: number; // RGBA mask
}

/**
 * Canonical WebGPU blend state for each {@link BlendMode}. Returns `null` for `None`
 * (no blending). Single source of truth shared by materials, the sprite batch, and
 * fullscreen passes — keep this in sync with nothing else (there is no other copy).
 */
export function blendStateFor(mode: BlendMode): BlendState | null {
  switch (mode) {
    case BlendMode.None:
      return null;
    case BlendMode.Additive:
      return {
        color: { srcFactor: 'src-alpha', dstFactor: 'one', operation: 'add' },
        alpha: { srcFactor: 'one', dstFactor: 'one', operation: 'add' },
      };
    case BlendMode.Multiply:
      return {
        color: { srcFactor: 'dst', dstFactor: 'one-minus-src-alpha', operation: 'add' },
        alpha: { srcFactor: 'dst-alpha', dstFactor: 'one-minus-src-alpha', operation: 'add' },
      };
    case BlendMode.Screen:
      return {
        color: { srcFactor: 'one', dstFactor: 'one-minus-src', operation: 'add' },
        alpha: { srcFactor: 'one', dstFactor: 'one-minus-src', operation: 'add' },
      };
    case BlendMode.PremultipliedAlpha:
      return {
        color: { srcFactor: 'one', dstFactor: 'one-minus-src-alpha', operation: 'add' },
        alpha: { srcFactor: 'one', dstFactor: 'one-minus-src-alpha', operation: 'add' },
      };
    case BlendMode.Normal:
    default:
      return {
        color: { srcFactor: 'src-alpha', dstFactor: 'one-minus-src-alpha', operation: 'add' },
        alpha: { srcFactor: 'one', dstFactor: 'one-minus-src-alpha', operation: 'add' },
      };
  }
}

export interface PipelineDescriptor {
  shader: ShaderHandle;
  vertexBuffers: VertexBufferLayout[];
  colorTargets: ColorTargetState[];
  depthStencil?: DepthStencilState;
  primitive?: {
    topology?: PrimitiveTopology;
    cullMode?: CullMode;
    frontFace?: FrontFace;
    usesTextures?: boolean; // If false, bind group only contains uniforms (no sampler/texture)
    textureCount?: number; // Number of textures (default: 1). Only used when usesTextures is true
  };
  /** Vertex shader entry point name (default: 'vs_main') */
  vertexEntryPoint?: string;
  /** Fragment shader entry point name (default: 'fs_main') */
  fragmentEntryPoint?: string;
  /**
   * Uniform block struct definition (from `defineStruct`).
   * When provided, WebGPU auto-maps uniform name → byte offset from the struct fields.
   * Field names in the struct must match the names used in `setUniform()` calls.
   */
  uniformDef?: { readonly fields: readonly { name: string; type: { readonly size: number; readonly components: number }; offset: number }[] };
  /**
   * Pipeline-overridable constants (WGSL `override` declarations).
   * These replace preprocessor `#define` for feature toggles —
   * the GPU compiler eliminates dead branches at pipeline creation.
   *
   * ```ts
   * constants: { USE_SHADOWS: true, MAX_LIGHTS: 128 }
   * ```
   *
   * In WGSL:
   * ```wgsl
   * override USE_SHADOWS: bool = false;
   * override MAX_LIGHTS: u32 = 64;
   * ```
   *
   * Note: boolean `true`/`false` are passed as `1`/`0` to WebGPU.
   */
  constants?: OverrideConstants;
  /**
   * When true, the pipeline includes a second bind group (group 1) for
   * lighting data: uniform buffer (LightUniforms) + read-only storage
   * buffer (lights array). Used by forward-lit sprite pipelines.
   */
  hasLighting?: boolean;
  /**
   * When true, the pipeline includes a second bind group (group 1) for
   * Forward+ lighting: uniform buffer (LightUniforms) + read-only storage
   * buffer (lights array) + read-only storage buffer (tile light indices).
   * Supersedes `hasLighting` — if both are set, `hasForwardPlus` takes precedence.
   */
  hasForwardPlus?: boolean;
  label: string;
}

// ============================================
// Render Commands
// ============================================

export enum CommandType {
  SetPipeline = 0,
  BindTexture = 1,
  BindVertexBuffer = 2,
  BindIndexBuffer = 3,
  SetUniforms = 4,
  Draw = 5,
  DrawIndexed = 6,
  SetViewport = 7,
  SetScissor = 8,
  Clear = 9,
}

export interface BaseCommand {
  type: CommandType;
  sortKey: number;
}

export interface SetPipelineCommand extends BaseCommand {
  type: CommandType.SetPipeline;
  pipeline: PipelineHandle;
}

export interface BindTextureCommand extends BaseCommand {
  type: CommandType.BindTexture;
  slot: number;
  texture: TextureHandle;
}

export interface BindVertexBufferCommand extends BaseCommand {
  type: CommandType.BindVertexBuffer;
  slot: number;
  buffer: BufferHandle;
  offset: number;
}

export interface BindIndexBufferCommand extends BaseCommand {
  type: CommandType.BindIndexBuffer;
  buffer: BufferHandle;
  format: 'uint16' | 'uint32';
  offset: number;
}

export interface SetUniformsCommand extends BaseCommand {
  type: CommandType.SetUniforms;
  data: Float32Array;
}

export interface DrawCommand extends BaseCommand {
  type: CommandType.Draw;
  vertexCount: number;
  instanceCount: number;
  firstVertex: number;
  firstInstance: number;
}

export interface DrawIndexedCommand extends BaseCommand {
  type: CommandType.DrawIndexed;
  indexCount: number;
  instanceCount: number;
  firstIndex: number;
  baseVertex: number;
  firstInstance: number;
}

export interface SetViewportCommand extends BaseCommand {
  type: CommandType.SetViewport;
  x: number;
  y: number;
  width: number;
  height: number;
  minDepth: number;
  maxDepth: number;
}

export interface SetScissorCommand extends BaseCommand {
  type: CommandType.SetScissor;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ClearCommand extends BaseCommand {
  type: CommandType.Clear;
  color?: [number, number, number, number];
  depth?: number;
  stencil?: number;
}

export type RenderCommand =
  | SetPipelineCommand
  | BindTextureCommand
  | BindVertexBufferCommand
  | BindIndexBufferCommand
  | SetUniformsCommand
  | DrawCommand
  | DrawIndexedCommand
  | SetViewportCommand
  | SetScissorCommand
  | ClearCommand;

// ============================================
// Sort Key Helper
// ============================================

/**
 * Pack layer, z-depth, and material into a single sort key
 * Layout: [layer:8][z:16][material:8] = 32 bits
 */
export function packSortKey(layer: number, z: number, material: number): number {
  // Clamp values
  layer = Math.max(0, Math.min(255, layer | 0));
  z = Math.max(0, Math.min(65535, ((z + 32768) | 0))); // offset z to handle negatives
  material = Math.max(0, Math.min(255, material | 0));
  
  return (layer << 24) | (z << 8) | material;
}

export function unpackSortKey(key: number): { layer: number; z: number; material: number } {
  return {
    layer: (key >>> 24) & 0xFF,
    z: ((key >>> 8) & 0xFFFF) - 32768,
    material: key & 0xFF,
  };
}

// ============================================
// Render Pass
// ============================================

export type LoadOp = 'load' | 'clear' | 'dont-care';
export type StoreOp = 'store' | 'discard';

export interface ColorAttachment {
  texture: TextureHandle;
  loadOp: LoadOp;
  storeOp: StoreOp;
  clearValue?: [number, number, number, number];
}

export interface DepthStencilAttachment {
  texture: TextureHandle;
  depthLoadOp: LoadOp;
  depthStoreOp: StoreOp;
  depthClearValue?: number;
  stencilLoadOp?: LoadOp;
  stencilStoreOp?: StoreOp;
  stencilClearValue?: number;
}

// ============================================
// MRT (Multiple Render Targets)
// ============================================

/**
 * Describes a single color attachment for an MRT render target.
 */
export interface MRTAttachmentDescriptor {
  /** Texture format for this attachment. */
  format: TextureFormat;
  /** Optional label for debugging. */
  label?: string;
}

/**
 * Descriptor for creating an MRT (Multiple Render Target) set.
 * Used for GBuffer and other multi-output rendering passes.
 */
export interface MRTRenderTargetDescriptor {
  width: number;
  height: number;
  /** One or more color attachments. */
  colorAttachments: MRTAttachmentDescriptor[];
  /** Whether to include a depth attachment. Default: false. */
  hasDepth?: boolean;
  /** Depth format. Default: 'depth24plus'. */
  depthFormat?: TextureFormat;
  /** Optional label for debugging. */
  label?: string;
}

/**
 * Info about a created MRT render target.
 * Returned by `getMRTRenderTargetInfo()`.
 */
export interface MRTRenderTargetInfo {
  /** Width in pixels. */
  width: number;
  /** Height in pixels. */
  height: number;
  /** Texture handles for each color attachment (index matches descriptor order). */
  colorTextures: TextureHandle[];
  /** Depth texture handle (or NULL_HANDLE if no depth). */
  depthTexture: TextureHandle;
  /** Number of color attachments. */
  attachmentCount: number;
}

// ============================================
// Material System v2
// ============================================

/**
 * Named texture slots for standard PBR-like 2D materials.
 * Slot indices map to GPU binding slots in the shader.
 */
export const TextureSlot = {
  /** RT0: Base color (albedo.rgb + alpha) */
  ALBEDO: 0,
  /** RT1: Normal map (2D signed XY) */
  NORMAL: 1,
  /** RT2: Emission (rgb + materialFlags) */
  EMISSION: 2,
  /** Mask / utility texture */
  MASK: 3,
} as const;

export type TextureSlotName = keyof typeof TextureSlot;
export type TextureSlotIndex = (typeof TextureSlot)[TextureSlotName];

/**
 * Material render pass tag — determines which render pass this material executes in.
 * - `opaque`: GBuffer write pass (deferred), z-sort front-to-back
 * - `transparent`: Forward pass with alpha blending, z-sort back-to-front
 * - `overlay`: UI / HUD overlay, drawn after all scene passes
 */
export type MaterialTag = 'opaque' | 'transparent' | 'overlay';

/**
 * Per-material render state overrides.
 * These map directly to WebGPU pipeline state.
 */
export interface RenderState {
  /** Blend mode (default: Normal for transparent, None for opaque) */
  blendMode?: BlendMode;
  /** Enable depth writes (default: true for opaque, false for transparent) */
  depthWrite?: boolean;
  /** Depth comparison function (default: 'less-equal') */
  depthCompare?: CompareFunction;
  /** Face culling mode (default: 'none′ for 2D) */
  cullMode?: CullMode;
  /** Stencil reference value */
  stencilRef?: number;
  /** Stencil read mask */
  stencilReadMask?: number;
  /** Stencil write mask */
  stencilWriteMask?: number;
  /** Stencil front face operation */
  stencilFront?: StencilFaceState;
  /** Stencil back face operation */
  stencilBack?: StencilFaceState;
}

/** Stencil operation per face (mirrors GPUStencilFaceState). */
export interface StencilFaceState {
  compare?: CompareFunction;
  failOp?: StencilOperation;
  depthFailOp?: StencilOperation;
  passOp?: StencilOperation;
}

export type StencilOperation =
  | 'keep'
  | 'zero'
  | 'replace'
  | 'invert'
  | 'increment-clamp'
  | 'decrement-clamp'
  | 'increment-wrap'
  | 'decrement-wrap';

// ============================================
// Sprite Vertex (for batching)
// ============================================

/**
 * Sprite vertex layout:
 * - position: float32x2 (8 bytes)
 * - uv: float32x2 (8 bytes)
 * - color: float16x4 (8 bytes) — HDR tint, components may exceed 1
 * - emission: float16x4 (8 bytes) — HDR emissive color (lit shaders; unlit ignores location 3)
 * Total: 32 bytes per vertex
 */
export const SPRITE_VERTEX_SIZE = 32;

export const SPRITE_VERTEX_LAYOUT: VertexBufferLayout = {
  stride: SPRITE_VERTEX_SIZE,
  stepMode: 'vertex',
  attributes: [
    { name: 'aPosition', format: 'float32x2', offset: 0, location: 0 },
    { name: 'aUV', format: 'float32x2', offset: 8, location: 1 },
    { name: 'aColor', format: 'float16x4', offset: 16, location: 2 },
    { name: 'aEmission', format: 'float16x4', offset: 24, location: 3 },
  ],
};

// ============================================
// Color Utils
// ============================================

/** Pack RGBA (0-255) into uint32 */
export function packColor(r: number, g: number, b: number, a: number): number {
  return ((a & 0xFF) << 24) | ((b & 0xFF) << 16) | ((g & 0xFF) << 8) | (r & 0xFF);
}

/** Pack RGBA (0-1) into uint32 */
export function packColorFloat(r: number, g: number, b: number, a: number): number {
  return packColor(
    (r * 255) | 0,
    (g * 255) | 0,
    (b * 255) | 0,
    (a * 255) | 0
  );
}

/** Unpack uint32 to RGBA (0-255) */
export function unpackColor(packed: number): [number, number, number, number] {
  return [
    packed & 0xFF,
    (packed >>> 8) & 0xFF,
    (packed >>> 16) & 0xFF,
    (packed >>> 24) & 0xFF,
  ];
}

// ============================================
// Stats
// ============================================

export interface RenderStats {
  drawCalls: number;
  triangles: number;
  vertices: number;
  textureBinds: number;
  pipelineChanges: number;
  bufferUploads: number;
  frameTime: number;
  computeDispatches: number;
  /** GPUBindGroup objects created this frame (cache misses). Lower is better. */
  bindGroupsCreated: number;
  /** queue.submit() calls this frame — ideally 1. */
  queueSubmits: number;
  // GPU memory (bytes)
  vramUsed: number;
  vramAllocated: number;
  // Resource counts
  textureCount: number;
  bufferCount: number;
  // Real GPU timing (timestamp queries) — optional; 0/empty when unavailable.
  /** GPU frame time in ms (min pass-begin → max pass-end). */
  gpuTime?: number;
  /** Per-marker GPU times for the last completed frame, sorted descending. */
  gpuPasses?: GpuPassTime[];
}

// ============================================
// Compute Pipeline
// ============================================

export interface ComputePipelineDescriptor {
  shader: ShaderHandle;
  /** Entry point function name (default: 'main') */
  entryPoint?: string;
  /** Workgroup size for validation (optional, usually defined in shader) */
  workgroupSize?: [number, number, number];
  /** 
   * Number of storage buffers (default: 3).
   * Bindings: 1, 2, 3 for first 3 buffers, then 5, 6, 7... for additional.
   * (Binding 4 is reserved for vfxVariables uniform)
   */
  storageBufferCount?: number;
  /**
   * Number of sampled textures (default: 0).
   * Textures use group 1, bindings 0, 1, 2, ...
   */
  textureCount?: number;
  /**
   * Pipeline-overridable constants (WGSL `override` declarations).
   * Same as `PipelineDescriptor.constants` but for compute shaders.
   */
  constants?: OverrideConstants;
  /** Debug label */
  label?: string;
}

export interface StorageBufferBinding {
  buffer: BufferHandle;
  offset?: number;
  size?: number;
}

export interface StorageTextureBinding {
  texture: TextureHandle;
  /** Access mode */
  access: 'read-only' | 'write-only' | 'read-write';
}

export interface ComputeBindGroup {
  /** Uniform buffers */
  uniforms?: BufferHandle[];
  /** Storage buffers (read/write) */
  storageBuffers?: StorageBufferBinding[];
  /** Storage textures */
  storageTextures?: StorageTextureBinding[];
  /** Sampled textures */
  textures?: TextureHandle[];
}

export interface DispatchSize {
  x: number;
  y?: number;
  z?: number;
}

// ============================================
// Shader Graph Support (future)
// ============================================

/**
 * Shader parameter types for future shader graph support
 */
export type ShaderParamType =
  | 'float'
  | 'float2'
  | 'float3'
  | 'float4'
  | 'int'
  | 'int2'
  | 'int3'
  | 'int4'
  | 'uint'
  | 'uint2'
  | 'uint3'
  | 'uint4'
  | 'mat2'
  | 'mat3'
  | 'mat4'
  | 'texture2d'
  | 'sampler';

/**
 * Shader parameter descriptor for introspection
 */
export interface ShaderParameter {
  name: string;
  type: ShaderParamType;
  binding?: number;
  group?: number;
  /** Default value if any */
  default?: number | number[];
  /** UI hints for editor */
  ui?: {
    label?: string;
    min?: number;
    max?: number;
    step?: number;
  };
}

/**
 * Shader metadata for shader graph integration
 */
export interface ShaderMetadata {
  name: string;
  type: 'vertex' | 'fragment' | 'compute';
  parameters: ShaderParameter[];
  /** Inputs (for fragment/compute) */
  inputs?: string[];
  /** Outputs */
  outputs?: string[];
}
